import classNames from 'classnames';
import { Box, useTheme } from '@mui/material';
import { DraggableEventHandler } from 'react-draggable';
import { MutableRefObject, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMount, useWindowScroll } from 'react-use';
import { trackEvent, getWindow } from '@surfline/web-common';

import type { ConditionData, ConditionRating } from 'types/conditionRating';
import type { SubregionOverviewData, SubregionDaySummaryData } from 'types/subregion';
import type { SurfData } from 'types/surf';
import type { WindData } from 'types/wind';

import Disclosure from 'components/Icons/Disclosure';
import { daySelectorDataTransformer } from 'components/ForecastDaySelector/utils/daySelectorDataProcessor';
import ForecastDaySelector from 'components/ForecastDaySelector';
import { useRouter } from 'next/router';
import { spotReportPath } from 'utils/urls';
import { getInitialDaySelectorStartDay } from '../utils';

import styles from './GraphDaySelectorContainer.module.scss';

interface Props {
  daysAllowed: number;
  daysPerRange: number;
  daySummary: SubregionDaySummaryData;
  daysToDisplay: Array<number>;
  firstDayIndex: number;
  graphContainerRef: MutableRefObject<HTMLDivElement | null>;
  graphHeight?: number;
  graphTop?: number;
  initialStartDayIndex?: number;
  isHistoricView: boolean;
  isPaywallActive: boolean;
  isTableViewMode: boolean;
  lastDayIndex: number;
  nextButtonDisabled: boolean;
  previousButtonDisabled: boolean;
  previousStartDay: number;
  ratings: ConditionData;
  referrer?: string;
  selectedStartDay: number;
  setPreviousStartDay: (dayIndex: number) => void;
  setSelectedStartDay: (dayIndex: number) => void;
  spotId: string;
  spotName: string;
  subregionOverview: SubregionOverviewData;
  surf: SurfData;
  swipeLeftCount: number;
  swipeRightCount: number;
  wind: WindData;
}

const DAY_SELECTOR_DAY_WIDTH = 75;

const GraphDaySelectorContainer: React.FC<Props> = ({
  daysAllowed,
  daysPerRange,
  daySummary,
  daysToDisplay,
  firstDayIndex,
  graphContainerRef,
  graphHeight,
  graphTop,
  initialStartDayIndex,
  isHistoricView,
  isPaywallActive,
  isTableViewMode,
  lastDayIndex,
  nextButtonDisabled,
  previousButtonDisabled,
  previousStartDay,
  ratings,
  referrer,
  selectedStartDay,
  setPreviousStartDay,
  setSelectedStartDay,
  spotId,
  spotName,
  subregionOverview,
  surf,
  swipeLeftCount,
  swipeRightCount,
  wind,
}) => {
  const router = useRouter();
  const win = getWindow();
  const activeDaySelectorDayRef = useRef<HTMLButtonElement | null>(null);
  const [currentPosition, setCurrentPosition] = useState({
    xRate: 0,
    yRate: 0,
  });
  const [shouldTrackNextRangeUpdate, setShouldTrackNextRangeUpdate] = useState(false);
  const prevNextDayButtonsRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();

  const daySelectorData = useMemo(
    () =>
      daySelectorDataTransformer({
        ratings: ratings as { days: Array<ConditionRating[]> },
        spotId,
        surf,
        wind,
      }),
    [ratings, spotId, surf, wind],
  );

  const windowScroll = useWindowScroll();
  const prevNextDayButtonsTop = prevNextDayButtonsRef?.current?.getBoundingClientRect()?.top ?? 0;

  const prevNextDayButtonsOffsetTop = useMemo(() => {
    const bodyRect = win?.document?.body?.getBoundingClientRect();
    const offset = prevNextDayButtonsTop - (bodyRect?.top ?? 0) + 3;
    return offset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevNextDayButtonsTop]);

  const prevNextDayButtonsScrollEnd = useMemo(
    () => (graphHeight ?? 0) + (graphTop ?? 0),
    [graphHeight, graphTop],
  );

  const prevNextDayButtonsSticky = useMemo(
    () =>
      prevNextDayButtonsOffsetTop === null
        ? false
        : windowScroll?.y > prevNextDayButtonsOffsetTop &&
          windowScroll?.y < prevNextDayButtonsScrollEnd,
    [prevNextDayButtonsOffsetTop, prevNextDayButtonsScrollEnd, windowScroll?.y],
  );

  const onNextDayClick = useCallback(() => {
    setShouldTrackNextRangeUpdate(true);
    setPreviousStartDay(selectedStartDay);
    setSelectedStartDay(selectedStartDay + 1);
    setCurrentPosition({ xRate: (selectedStartDay + 1) * DAY_SELECTOR_DAY_WIDTH, yRate: 0 });
    if (!prevNextDayButtonsSticky) {
      activeDaySelectorDayRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [setPreviousStartDay, selectedStartDay, setSelectedStartDay, prevNextDayButtonsSticky]);

  const onPreviousDayClick = useCallback(() => {
    setShouldTrackNextRangeUpdate(true);
    setPreviousStartDay(selectedStartDay);
    setSelectedStartDay(selectedStartDay - 1);
    setCurrentPosition({ xRate: (selectedStartDay - 1) * DAY_SELECTOR_DAY_WIDTH, yRate: 0 });
    if (!prevNextDayButtonsSticky) {
      activeDaySelectorDayRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [prevNextDayButtonsSticky, selectedStartDay, setPreviousStartDay, setSelectedStartDay]);

  const onDragDaySelectorStop: DraggableEventHandler = useCallback(
    (e, data) => {
      setPreviousStartDay(data.x / DAY_SELECTOR_DAY_WIDTH);
      setSelectedStartDay(data.x / DAY_SELECTOR_DAY_WIDTH);
      setCurrentPosition({
        xRate: data.x,
        yRate: data.y,
      });
    },
    [setPreviousStartDay, setSelectedStartDay],
  );

  const onScreenResize = useCallback(() => {
    if (daysToDisplay[daysToDisplay.length - 1] >= 15 && daysToDisplay.length < daysPerRange) {
      setPreviousStartDay(selectedStartDay);
      setSelectedStartDay(16 - daysPerRange);
      setCurrentPosition({ xRate: (16 - daysPerRange) * DAY_SELECTOR_DAY_WIDTH, yRate: 0 });
    }
  }, [daysPerRange, daysToDisplay, selectedStartDay, setPreviousStartDay, setSelectedStartDay]);

  const segmentPropertiesDaySelectorPaywall = useMemo(
    () => ({
      category: 'cams & reports',
      pageName: 'Spot Forecast',
      spotId,
      subregionId: subregionOverview?._id,
      location: 'Forecast Range',
    }),
    [spotId, subregionOverview?._id],
  );

  const paywall = useMemo(
    () => ({
      isActive: isPaywallActive,
      referrer,
      segmentProperties: segmentPropertiesDaySelectorPaywall,
    }),
    [isPaywallActive, referrer, segmentPropertiesDaySelectorPaywall],
  );

  const setDaySelectorDays = useCallback(
    (startDayIndex: number) => {
      if (isTableViewMode) {
        const newUrl = spotReportPath(
          {
            ...router?.query,
            camId: router?.query?.camId?.toString(),
            initialStartDayIndex: startDayIndex,
            startDate: router?.query?.startDate?.toString(),
            view: isTableViewMode ? 'table' : undefined,
          },
          {
            _id: spotId,
            name: spotName ?? '',
          },
        );
        router.push(newUrl, undefined, { shallow: true, scroll: false });
      } else {
        setShouldTrackNextRangeUpdate(true);
        if (daysToDisplay.some((i) => i === startDayIndex)) return null;
        setPreviousStartDay(startDayIndex);
        if (startDayIndex > previousStartDay) {
          setCurrentPosition({
            xRate: (startDayIndex - (daysToDisplay.length - 1)) * DAY_SELECTOR_DAY_WIDTH,
            yRate: 0,
          });
          return setSelectedStartDay(startDayIndex - daysToDisplay.length + 1);
        }
        setCurrentPosition({ xRate: startDayIndex * DAY_SELECTOR_DAY_WIDTH, yRate: 0 });
      }

      return setSelectedStartDay(startDayIndex);
    },
    [
      daysToDisplay,
      isTableViewMode,
      previousStartDay,
      router,
      setPreviousStartDay,
      setSelectedStartDay,
      spotId,
      spotName,
    ],
  );

  const trackForecastRangeClicked = useCallback(() => {
    trackEvent('Forecast Range Updated', {
      forecastStart: firstDayIndex + 1,
      forecastEnd: lastDayIndex + 1,
      spotId,
      spotName,
      subregionId: subregionOverview?._id,
      subregionName: subregionOverview?.name,
      pageName: 'forecast',
      uiElement: 'day selector',
      action: 'click',
    });
  }, [
    firstDayIndex,
    lastDayIndex,
    spotId,
    spotName,
    subregionOverview?._id,
    subregionOverview?.name,
  ]);

  const stickyButtonRightPos = useMemo(
    () =>
      `${
        ((win?.innerWidth ?? 0) - (graphContainerRef?.current?.clientWidth ?? 0)) / 2 - 8
      }px !important`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graphContainerRef?.current?.clientWidth],
  );

  useMount(() => {
    if (typeof initialStartDayIndex === 'number') {
      const startDay = getInitialDaySelectorStartDay(
        initialStartDayIndex,
        daysPerRange,
        daysAllowed,
      );
      setSelectedStartDay(startDay);
      setCurrentPosition({ xRate: startDay * DAY_SELECTOR_DAY_WIDTH, yRate: 0 });
      setPreviousStartDay(startDay);
    }
  });

  useEffect(() => {
    if (shouldTrackNextRangeUpdate) {
      setShouldTrackNextRangeUpdate(false);
      trackForecastRangeClicked();
    }
    // Only run this when daysToDisplay is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysToDisplay]);

  useEffect(() => {
    if (swipeRightCount > 0 && !!onPreviousDayClick) {
      onPreviousDayClick();
    }
    // Only run this when swipeLeftCount is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swipeRightCount]);

  useEffect(() => {
    if (swipeLeftCount > 0 && !!onNextDayClick) {
      onNextDayClick();
    }
    // Only run this when swipeRightCount is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swipeLeftCount]);

  return (
    <>
      <Box
        className={classNames({
          [styles.daySelectorGraphContainer]: true,
          [styles.hasHighlights]: true,
          [styles.daySelectorGraphContainerLoading]: surf?.loading,
        })}
      >
        <ForecastDaySelector
          activeRef={activeDaySelectorDayRef}
          currentPosition={currentPosition}
          data={daySelectorData}
          daysAllowed={daysAllowed}
          daysToDisplay={daysToDisplay}
          daysPerRange={daysPerRange}
          daySummary={daySummary}
          error={surf?.error}
          loading={surf?.loading}
          isHistoricView={isHistoricView}
          onDragDaySelectorStop={onDragDaySelectorStop}
          onScreenResize={onScreenResize}
          paywall={paywall}
          setDaysToDisplay={setDaySelectorDays}
          isTableViewMode={isTableViewMode}
        />
      </Box>
      {!isTableViewMode && (
        <Box>
          <Box
            className={styles.scrubberButtons}
            ref={prevNextDayButtonsRef}
            zIndex={prevNextDayButtonsSticky ? 0 : 11}
          >
            <Disclosure
              ariaLabel="previous"
              color={
                previousButtonDisabled
                  ? theme.palette.text.light
                  : theme.palette.text.brandSecondary
              }
              disabled={previousButtonDisabled}
              onClick={onPreviousDayClick}
              rotation={270}
              size={11}
            />

            <Disclosure
              ariaLabel="next"
              color={
                nextButtonDisabled ? theme.palette.text.light : theme.palette.text.brandSecondary
              }
              disabled={nextButtonDisabled}
              onClick={onNextDayClick}
              rotation={90}
              size={11}
            />
          </Box>

          {/* replicate button group to avoid making changes on Box with ref */}
          {prevNextDayButtonsSticky && (
            <Box
              className={classNames(styles.scrubberButtons, styles.stickyScrubberButtons)}
              right={stickyButtonRightPos}
            >
              <Disclosure
                ariaLabel="previous"
                color={
                  previousButtonDisabled
                    ? theme.palette.text.light
                    : theme.palette.text.brandSecondary
                }
                disabled={previousButtonDisabled}
                onClick={onPreviousDayClick}
                rotation={270}
                size={11}
              />

              <Disclosure
                ariaLabel="next"
                color={
                  nextButtonDisabled ? theme.palette.text.light : theme.palette.text.brandSecondary
                }
                disabled={nextButtonDisabled}
                onClick={onNextDayClick}
                rotation={90}
                size={11}
              />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default memo(GraphDaySelectorContainer);
